import { products } from './data/products.js';

// DOM Elements
const gridContainer = document.getElementById('product-grid');
const emptyState = document.getElementById('empty-state');
const resultsCountEl = document.getElementById('results-count');
const searchInput = document.getElementById('search-input');
const searchKbd = document.getElementById('search-kbd');
const searchClearBtn = document.getElementById('search-clear');
const filterTabsContainer = document.getElementById('filter-tabs');
const emptyResetBtn = document.getElementById('empty-reset-btn');
const totalProjectsStat = document.getElementById('total-projects-stat');

// State
let currentCategory = 'all';
let searchQuery = '';

/**
 * Initializes the application
 */
function init() {
  renderCategoryTabs();
  renderProducts();
  setupEventListeners();
  
  if (totalProjectsStat) {
    totalProjectsStat.textContent = products.length.toString();
  }
}

/**
 * Dynamically builds category filter tabs based on products catalog
 */
function renderCategoryTabs() {
  if (!filterTabsContainer) return;

  // Collect unique categories in order of appearance
  const categories = [...new Set(products.map((p) => p.category))];

  // Category counts map
  const counts = {
    all: products.length,
  };
  categories.forEach((cat) => {
    counts[cat] = products.filter((p) => p.category.toLowerCase() === cat.toLowerCase()).length;
  });

  // Construct tab buttons HTML
  let tabsHtml = `
    <button class="filter-tab ${currentCategory === 'all' ? 'active' : ''}" data-category="all" role="tab" aria-selected="${currentCategory === 'all'}">
      All <span class="tab-count">${counts.all}</span>
    </button>
  `;

  categories.forEach((cat) => {
    const isActive = currentCategory.toLowerCase() === cat.toLowerCase();
    tabsHtml += `
      <button class="filter-tab ${isActive ? 'active' : ''}" data-category="${escapeHtml(cat)}" role="tab" aria-selected="${isActive}">
        ${escapeHtml(cat)} <span class="tab-count">${counts[cat]}</span>
      </button>
    `;
  });

  filterTabsContainer.innerHTML = tabsHtml;
}

/**
 * Filters the product list based on category and search query
 */
function getFilteredProducts() {
  return products.filter((item) => {
    // Category match
    const categoryMatch =
      currentCategory === 'all' ||
      item.category.toLowerCase() === currentCategory.toLowerCase();

    // Search query match
    if (!searchQuery.trim()) {
      return categoryMatch;
    }

    const query = searchQuery.toLowerCase().trim();
    const titleMatch = item.title.toLowerCase().includes(query);
    const taglineMatch = item.tagline.toLowerCase().includes(query);
    const descMatch = item.description.toLowerCase().includes(query);
    const techMatch = item.technologies.some((tech) =>
      tech.toLowerCase().includes(query)
    );

    return categoryMatch && (titleMatch || taglineMatch || descMatch || techMatch);
  });
}

/**
 * Renders the filtered products onto the grid
 */
function renderProducts() {
  const filtered = getFilteredProducts();

  // Update counter
  if (resultsCountEl) {
    const text = filtered.length === 1 ? 'Showing 1 active app' : `Showing ${filtered.length} active apps`;
    resultsCountEl.textContent = text;
  }

  // Handle empty state
  if (filtered.length === 0) {
    gridContainer.innerHTML = '';
    emptyState.classList.remove('hidden');
    return;
  }

  emptyState.classList.add('hidden');

  // Render cards
  const cardsHtml = filtered.map((item) => createProductCardMarkup(item)).join('');
  gridContainer.innerHTML = cardsHtml;
}

/**
 * Generates card HTML for a single product
 */
function createProductCardMarkup(item) {
  const isLive = item.status.toLowerCase() === 'live';
  const statusClass = isLive ? 'live' : 'beta';

  const techTagsHtml = item.technologies
    .map((tech) => `<span class="tech-tag">${escapeHtml(tech)}</span>`)
    .join('');

  const githubBtnHtml = item.githubUrl
    ? `
      <a 
        href="${escapeHtml(item.githubUrl)}" 
        target="_blank" 
        rel="noopener noreferrer" 
        class="btn-github" 
        aria-label="View source code on GitHub"
        title="View Source Code"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
        </svg>
        <span>Code</span>
      </a>
    `
    : '';

  return `
    <article class="product-card" id="card-${escapeHtml(item.id)}">
      <!-- Card Banner -->
      <div class="card-banner" style="background: ${item.bannerGradient || 'var(--bg-subtle)'};">
        <div class="card-icon-bubble" style="color: ${item.accentColor || 'var(--text-primary)'};">
          ${item.icon || '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg>'}
        </div>
        <div class="card-badges-top">
          ${item.featured ? '<span class="featured-pill">Core App</span>' : ''}
          <span class="status-badge ${statusClass}">${escapeHtml(item.status)}</span>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body">
        <span class="card-category">${escapeHtml(item.category)}</span>
        <h2 class="card-title">${escapeHtml(item.title)}</h2>
        <p class="card-tagline">${escapeHtml(item.tagline)}</p>
        <p class="card-description">${escapeHtml(item.description)}</p>

        <!-- Tech tags -->
        <div class="card-tech-list">
          ${techTagsHtml}
        </div>

        <!-- Action buttons -->
        <div class="card-actions">
          <a 
            href="${escapeHtml(item.url)}" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="btn-launch"
          >
            <span>Launch App</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="7" y1="17" x2="17" y2="7"></line>
              <polyline points="7 7 17 7 17 17"></polyline>
            </svg>
          </a>
          ${item.guideUrl ? `
            <a 
              href="${escapeHtml(item.guideUrl)}" 
              class="btn-guide-card"
              title="View ${escapeHtml(item.guideLabel || 'User Guide')}"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 1-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              <span>${escapeHtml(item.guideLabel || 'Guide')}</span>
            </a>
          ` : ''}
          ${githubBtnHtml}
        </div>
      </div>
    </article>
  `;
}

/**
 * Setup UI event listeners
 */
function setupEventListeners() {
  // Category tabs
  filterTabsContainer.addEventListener('click', (e) => {
    const tabBtn = e.target.closest('.filter-tab');
    if (!tabBtn) return;

    // Update active tab styles
    filterTabsContainer.querySelectorAll('.filter-tab').forEach((tab) => {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
    });
    tabBtn.classList.add('active');
    tabBtn.setAttribute('aria-selected', 'true');

    currentCategory = tabBtn.dataset.category;
    renderProducts();
  });

  // Search input
  searchInput.addEventListener('input', (e) => {
    searchQuery = e.target.value;
    toggleSearchControls();
    renderProducts();
  });

  // Clear search
  searchClearBtn.addEventListener('click', () => {
    searchInput.value = '';
    searchQuery = '';
    toggleSearchControls();
    searchInput.focus();
    renderProducts();
  });

  // Reset filters from empty state
  if (emptyResetBtn) {
    emptyResetBtn.addEventListener('click', () => {
      searchInput.value = '';
      searchQuery = '';
      currentCategory = 'all';

      // Re-render tabs UI
      renderCategoryTabs();
      toggleSearchControls();
      renderProducts();
    });
  }

  // Keyboard shortcut '/' to focus search
  window.addEventListener('keydown', (e) => {
    if (e.key === '/' && document.activeElement !== searchInput) {
      e.preventDefault();
      searchInput.focus();
    } else if (e.key === 'Escape' && document.activeElement === searchInput) {
      searchInput.blur();
    }
  });
}

/**
 * Toggle visibility of keyboard hint vs clear button
 */
function toggleSearchControls() {
  if (searchInput.value.length > 0) {
    searchClearBtn.classList.add('visible');
    searchKbd.style.display = 'none';
  } else {
    searchClearBtn.classList.remove('visible');
    searchKbd.style.display = 'block';
  }
}

/**
 * Basic HTML escaping helper
 */
function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Start app
init();
